(function () {
  "use strict";

  if (window.matchMedia("(max-width: 767px)").matches) return;

  function waitForLibs(cb) {
    const check = () => {
      if (window.THREE && window.gsap && window.ScrollTrigger) {
        cb();
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  }

  waitForLibs(function () {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    const container = document.querySelector(".rs-below-fold--howto");
    const section = container?.querySelector(".bezel-howto");
    const cinematic = section?.querySelector(".bezel-howto__cinematic");
    const canvas = cinematic?.querySelector(".bezel-howto__canvas");
    const progress = cinematic?.querySelector(".bezel-howto__progress-bar");
    const progressContainer = cinematic?.querySelector(".bezel-howto__progress");
    const steps = cinematic?.querySelectorAll(".howto-step") || [];
    const howtoImages = cinematic?.querySelectorAll(".howto-img") || [];
    if (!container || !section || !cinematic || !canvas || steps.length === 0) return;

    function viewportSize() {
      return {
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight,
      };
    }

    function isMobileLayout() {
      return viewportSize().width < 768;
    }

    function getLayout() {
      const { width } = viewportSize();
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;

      if (mobile) {
        return {
          ringScale: 0.58,
          ringX: 0,
          ringY: -0.35,
          ringSway: 0.18,
          cameraZ: 6.8,
          particleOpacity: 0.22,
        };
      }

      if (tablet) {
        return {
          ringScale: 0.82,
          ringX: 0.55,
          ringY: 0,
          ringSway: 0.35,
          cameraZ: 5.6,
          particleOpacity: 0.32,
        };
      }

      return {
        ringScale: 1,
        ringX: 1.2,
        ringY: 0,
        ringSway: 0.5,
        cameraZ: 5,
        particleOpacity: 0.4,
      };
    }

    let layout = getLayout();
    let clipDirty = true;
    let sectionInView = false;

    // Three.js scene — glowing rotating torus (bezel shape)
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = layout.cameraZ;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);

    const geometry = new THREE.TorusGeometry(1.6, 0.12, 64, 128);
    const material = new THREE.MeshStandardMaterial({
      color: 0xc8c4bc,
      metalness: 0.95,
      roughness: 0.2,
      emissive: 0x0c2340,
      emissiveIntensity: 0.15,
    });
    const ring = new THREE.Mesh(geometry, material);
    ring.rotation.x = Math.PI * 0.35;
    scene.add(ring);

    const innerGeo = new THREE.TorusGeometry(1.35, 0.06, 32, 128);
    const innerMat = new THREE.MeshStandardMaterial({
      color: 0x0c2340,
      metalness: 0.9,
      roughness: 0.3,
      emissive: 0x0c2340,
      emissiveIntensity: 0.3,
    });
    const innerRing = new THREE.Mesh(innerGeo, innerMat);
    innerRing.rotation.x = Math.PI * 0.35;
    scene.add(innerRing);

    const particleCount = isMobileLayout() ? 150 : 300;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 12;
    }
    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const particleMat = new THREE.PointsMaterial({
      size: 0.02,
      color: 0xffffff,
      transparent: true,
      opacity: layout.particleOpacity,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));

    const pointLight = new THREE.PointLight(0xffffff, 1.5, 10);
    pointLight.position.set(3, 3, 5);
    scene.add(pointLight);

    const rimLight = new THREE.PointLight(0x4488ff, 0.8, 8);
    rimLight.position.set(-3, -2, 3);
    scene.add(rimLight);

    let scrollProgress = 0;
    let activeStepIndex = 0;
    const totalSteps = steps.length;

    function markClipDirty() {
      clipDirty = true;
    }

    function setSectionVisible(visible) {
      sectionInView = visible;
      section.classList.toggle("is-in-view", visible);
      progressContainer?.classList.toggle("is-visible", visible);
      if (!visible) {
        steps.forEach((s) => s.classList.remove("is-active"));
        howtoImages.forEach((img) => img.classList.remove("is-active"));
      } else {
        updateSteps();
      }
      markClipDirty();
    }

    function stepFromProgress(progress) {
      if (totalSteps <= 1) return 0;
      // Delay handoffs so each step stays readable while scrolling.
      return Math.min(totalSteps - 1, Math.max(0, Math.floor(progress * totalSteps - 0.35)));
    }

    ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: "bottom bottom",
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        scrollProgress = self.progress;
        if (progress) progress.style.width = `${scrollProgress * 100}%`;
        const newStep = stepFromProgress(scrollProgress);
        if (newStep !== activeStepIndex) {
          activeStepIndex = newStep;
          updateSteps();
        }
        markClipDirty();
      },
      onEnter: () => {
        setSectionVisible(true);
      },
      onLeave: () => {
        setSectionVisible(false);
      },
      onEnterBack: () => {
        setSectionVisible(true);
      },
      onLeaveBack: () => {
        setSectionVisible(false);
      },
    });

    function updateSteps() {
      steps.forEach((step, i) => {
        step.classList.toggle("is-active", i === activeStepIndex);
      });
      howtoImages.forEach((img) => {
        const imgStep = parseInt(img.dataset.step, 10);
        img.classList.toggle("is-active", imgStep === activeStepIndex);
      });
    }

    // Visually crop the fixed, full-viewport canvas to the section's on-screen
    // rectangle so the torus only shows inside the bounds of the how-to section.
    function clipCanvasToSection() {
      const rect = section.getBoundingClientRect();
      const { width, height } = viewportSize();
      const topInset = Math.max(0, rect.top);
      const bottomInset = Math.max(0, height - rect.bottom);
      const leftInset = Math.max(0, rect.left);
      const rightInset = Math.max(0, width - rect.right);
      canvas.style.clipPath = `inset(${topInset}px ${rightInset}px ${bottomInset}px ${leftInset}px)`;
      clipDirty = false;
    }

    function applyLayout() {
      layout = getLayout();
      ring.scale.setScalar(layout.ringScale);
      innerRing.scale.setScalar(layout.ringScale);
      particleMat.opacity = layout.particleOpacity;
      camera.position.z = layout.cameraZ;
    }

    function resizeRenderer() {
      const { width, height } = viewportSize();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobileLayout() ? 1.5 : 2));
      markClipDirty();
    }

    function animate() {
      requestAnimationFrame(animate);

      if (clipDirty) {
        clipCanvasToSection();
      }

      if (!sectionInView) return;

      ring.rotation.z = scrollProgress * Math.PI * 4;
      ring.rotation.x = Math.PI * 0.35 + scrollProgress * 0.5;
      ring.position.x = layout.ringX + Math.sin(scrollProgress * Math.PI) * layout.ringSway;
      ring.position.y = layout.ringY + Math.cos(scrollProgress * Math.PI * 2) * 0.3;

      innerRing.rotation.z = -scrollProgress * Math.PI * 3;
      innerRing.rotation.x = Math.PI * 0.35 + scrollProgress * 0.3;
      innerRing.position.x = ring.position.x;
      innerRing.position.y = ring.position.y;

      const separation =
        scrollProgress > 0.4 && scrollProgress < 0.7
          ? Math.sin(((scrollProgress - 0.4) * Math.PI) / 0.3) * 0.6 * layout.ringScale
          : 0;
      ring.position.z = separation;
      innerRing.position.z = -separation * 0.3;

      particles.rotation.y = scrollProgress * 0.5;
      particles.rotation.x = scrollProgress * 0.2;
      material.emissiveIntensity = 0.15 + Math.sin(scrollProgress * Math.PI * 6) * 0.08;

      camera.position.x = Math.sin(scrollProgress * Math.PI) * 0.3 * layout.ringScale;
      camera.position.y =
        layout.ringY * 0.35 + Math.cos(scrollProgress * Math.PI * 2) * 0.15 * layout.ringScale;
      camera.lookAt(ring.position);

      renderer.render(scene, camera);
    }

    function onResize() {
      applyLayout();
      resizeRenderer();
      ScrollTrigger.refresh();
    }

    applyLayout();
    resizeRenderer();
    window.addEventListener("resize", onResize, { passive: true });
    window.visualViewport?.addEventListener("resize", onResize, { passive: true });
    window.visualViewport?.addEventListener("scroll", markClipDirty, { passive: true });
    window.addEventListener("scroll", markClipDirty, { passive: true });

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(section);
      resizeObserver.observe(container);
    }

    animate();
    ScrollTrigger.refresh();
  });
})();
